import { type ComponentProps, useState } from 'react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { ConfirmDialog } from '@/modules/ui/components/confirm-dialog/ConfirmDialog';

type ResponseLinkProps = ComponentProps<'a'>;

const linkClassName = 'wrap-anywhere font-medium text-primary underline';
// Streamdown's target for a link whose URL hasn't streamed in yet.
const incompleteHref = 'streamdown:incomplete-link';

/**
 * A link in a Markdown reply. A link into this app navigates in place; one to another site asks first, since a
 * reply's links come from a model and could point anywhere.
 */
export function ResponseLink({
  href,
  title,
  children,
  className,
}: ResponseLinkProps) {
  const [confirming, setConfirming] = useState(false);
  // A link still streaming in has no target yet.
  const url = href && href !== incompleteHref ? parseUrl(href) : null;
  if (!url) {
    return <span className={cn(linkClassName, className)}>{children}</span>;
  }

  if (url.origin === window.location.origin) {
    return (
      <Link
        className={cn(linkClassName, className)}
        title={title}
        to={url.pathname + url.search + url.hash}
      >
        {children}
      </Link>
    );
  }

  const isWeb = url.protocol === 'http:' || url.protocol === 'https:';
  // A middle click opens the link too, so it asks as well. The href stays, so the link can still be copied.
  const confirm = (event: React.MouseEvent) => {
    if (!isWeb) return;
    event.preventDefault();
    setConfirming(true);
  };

  return (
    <>
      <a
        className={cn(linkClassName, className)}
        href={url.href}
        onAuxClick={(event) => {
          if (event.button === 1) confirm(event);
        }}
        onClick={confirm}
        rel="noopener noreferrer"
        target="_blank"
        title={title}
      >
        {children}
      </a>
      {isWeb && (
        <ConfirmDialog
          confirmText="Open link"
          description={`This link leaves Finlay for ${url.hostname}.`}
          onConfirm={() => window.open(url.href, '_blank', 'noopener')}
          onOpenChange={setConfirming}
          open={confirming}
          title="Open external link?"
        />
      )}
    </>
  );
}

const parseUrl = (href: string) => {
  try {
    return new URL(href, window.location.href);
  } catch {
    return null;
  }
};
